/** @jsxImportSource @emotion/react */
import { compact } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ScanTemplateDataMutators } from '@kaiizen/kai-pro-api-react-state';
import { createTemplateTask, ScanTemplateGroup, ScanTemplatePartContext, ScanTemplateRoot, ScanTemplateTask } from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button, PageSubHeaderBar } from '../../Widgets';
import { TemplateTasksEditor } from './TemplateTasksEditor';

const GROUP_EDITOR_CONTAINER_STYLE = css`
  label: GROUP_EDITOR_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  background: var(--background);
  overflow: hidden;
`;

export const ScanTemplateGroupEditor = ({
  group,
  filteredTaskIds,
  scanTemplateId,
  selectedScanTemplatePartContext,
  setSelectedScanTemplatePartContext,
  templateRoot,
  filterRegex,
  scanTemplateDataMutators,
  readOnly,
}: {
  group: ScanTemplateGroup;
  filteredTaskIds: string[];
  scanTemplateId: string;
  selectedScanTemplatePartContext: ScanTemplatePartContext | null;
  setSelectedScanTemplatePartContext: (context: ScanTemplatePartContext | null) => void;
  templateRoot: ScanTemplateRoot;
  filterRegex?: RegExp;
  scanTemplateDataMutators: ScanTemplateDataMutators;
  readOnly?: boolean;
}) => {
  const navigate = useNavigate();
  // Get tasks for this group in order (don't sort)
  const groupTasks = useMemo(() => {
    const taskIds = group.taskTemplateIds || [];
    const inlineTasks = group.taskTemplates || [];

    // Map task IDs to actual tasks, maintaining order
    const referencedTasks = taskIds
      .map(taskId => templateRoot.taskTemplatesById?.[taskId])
      .filter((task): task is ScanTemplateTask => !!task);

    return [...referencedTasks, ...inlineTasks];
  }, [group.taskTemplateIds, group.taskTemplates, templateRoot.taskTemplatesById]);

  // Filter by regex but maintain order
  const filteredTasks = useMemo(
    () => compact(filteredTaskIds.map(taskId => templateRoot.taskTemplatesById?.[taskId])),
    [filteredTaskIds, templateRoot.taskTemplatesById],
  );

  const handleViewGroupDetails = () => {
    setSelectedScanTemplatePartContext({ groupId: group.id });
  };

  const title = filteredTasks.length < groupTasks.length
    ? `${group.name} (${filteredTasks.length}/${groupTasks.length} filtered tasks)`
    : group.name;

  return (
    <div css={GROUP_EDITOR_CONTAINER_STYLE}>
      <PageSubHeaderBar
        title={title}
        buttons={[
          ...(!readOnly
            ? [
              <Button
                key='addTask'
                onClick={() => {
                  const newTask = createTemplateTask({ name: 'New Task' });
                  scanTemplateDataMutators.addTask(group.id, newTask);
                  setTimeout(() => navigate(`/template/${scanTemplateId}/group/${group.id}?taskId=${newTask.id}`), 100);
                }}
              >
                Add Task
              </Button>,
            ]
            : []),
          <Button key='editGroupDetails' onClick={handleViewGroupDetails}>
            {readOnly ? 'View Group Details' : 'Edit Group Details'}
          </Button>,
        ]}
      />
      <TemplateTasksEditor
        groupId={group.id}
        tasks={filteredTasks}
        templateRoot={templateRoot}
        selectedScanTemplatePartContext={selectedScanTemplatePartContext}
        setSelectedScanTemplatePartContext={setSelectedScanTemplatePartContext}
        filterRegex={filterRegex}
      />
    </div>
  );
};
